import React, { useState } from 'react';
import {
    Button,
    Card,
    CardBody,
    CardHeader,
    Col,
    Input,
    Row,
} from 'reactstrap';
import Swal from 'sweetalert2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCheckCircle,
    faEye,
    faFileContract,
    faPlus,
    faShieldAlt,
    faTimesCircle,
    faUpload,
} from '@fortawesome/free-solid-svg-icons';
import {
    deleteComprobantePago,
    resolveOperacionDocumentoFetchUrl,
    uploadDocumentoOperacion,
} from '../api/pldApi';

export default function DocumentosTransaccionOperacion({
    operacion,
    setOperacion,
    unidadId,
    operacionId,
    puedeDocumentos,
}) {
    const [subiendoDoc, setSubiendoDoc] = useState(null);
    const [pendFormaPago, setPendFormaPago] = useState('');
    const [pendMonto, setPendMonto] = useState('');

    async function handleUploadDocTransaccion(e, docId) {
        const file = e.target.files && e.target.files[0];
        e.target.value = '';
        if (!file || !unidadId || !operacionId) return;
        setSubiendoDoc(docId);
        try {
            const res = await uploadDocumentoOperacion(unidadId, operacionId, docId, file);
            if (res && res.success) {
                setOperacion((prev) => prev ? { ...prev, [docId]: res.doc } : prev);
            } else {
                await Swal.fire({ icon: 'error', title: 'Subida', text: (res && res.error) || 'No se pudo subir.' });
            }
        } catch (err) {
            await Swal.fire({
                icon: 'error', title: 'Error',
                text: err?.response?.data?.error || err.message || 'No se pudo subir el archivo.',
            });
        } finally {
            setSubiendoDoc(null);
        }
    }

    async function handleUploadComprobantesPago(e) {
        const file = e.target.files && e.target.files[0];
        e.target.value = '';
        if (!file || !unidadId || !operacionId) return;
        setSubiendoDoc('comprobantes_pago');
        try {
            const res = await uploadDocumentoOperacion(
                unidadId, operacionId, 'comprobantes_pago', file,
                { forma_pago: pendFormaPago, forma_pago_monto: pendMonto }
            );
            if (res && res.success) {
                setOperacion((prev) => prev ? {
                    ...prev,
                    comprobantes_pago: [...(prev.comprobantes_pago || []), res.doc],
                } : prev);
                setPendFormaPago('');
                setPendMonto('');
                await Swal.fire({
                    icon: 'success',
                    title: 'Operación actualizada',
                    text: 'El comprobante se registró correctamente.',
                    timer: 1800,
                    showConfirmButton: false,
                });
            } else {
                await Swal.fire({ icon: 'error', title: 'Subida', text: (res && res.error) || 'No se pudo subir.' });
            }
        } catch (err) {
            await Swal.fire({
                icon: 'error', title: 'Error',
                text: err?.response?.data?.error || err.message || 'No se pudo subir el archivo.',
            });
        } finally {
            setSubiendoDoc(null);
        }
    }

    async function handleDeleteComprobante(idx) {
        const ok = await Swal.fire({
            icon: 'warning',
            title: '¿Eliminar comprobante?',
            text: 'Esta acción no se puede deshacer.',
            showCancelButton: true,
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#dc3545',
        });
        if (!ok.isConfirmed) return;
        try {
            await deleteComprobantePago(unidadId, operacionId, idx);
            setOperacion((prev) => prev ? {
                ...prev,
                comprobantes_pago: (prev.comprobantes_pago || []).filter((_, i) => i !== idx),
            } : prev);
        } catch (err) {
            await Swal.fire({
                icon: 'error', title: 'Error',
                text: err?.response?.data?.error || err.message || 'No se pudo eliminar el comprobante.',
            });
        }
    }

    function renderDocumentoSingle(docId, titulo) {
        const doc = operacion[docId];
        const tieneArchivo = doc && doc.fileUrl;
        const subiendo = subiendoDoc === docId;
        const docUrl = tieneArchivo
            ? resolveOperacionDocumentoFetchUrl(unidadId, operacionId, doc)
            : null;
        return (
            <div className="mb-3">
                <p className="small text-muted text-uppercase font-weight-bold mb-1">
                    <FontAwesomeIcon icon={faFileContract} className="mr-1" />
                    {titulo}
                </p>
                <label
                    className={`btn btn-sm btn-block d-flex justify-content-between align-items-center mb-0${tieneArchivo ? ' btn-success' : ' btn-outline-secondary'}${(subiendo || !puedeDocumentos) ? ' disabled' : ''}`}
                    style={{ cursor: (subiendo || !puedeDocumentos) ? 'not-allowed' : 'pointer', fontWeight: 'normal', fontSize: '0.8rem' }}
                >
                    <span>
                        <FontAwesomeIcon
                            icon={subiendo ? faUpload : tieneArchivo ? faCheckCircle : faUpload}
                            spin={subiendo}
                            className="mr-1"
                        />
                        {subiendo ? 'Subiendo…' : tieneArchivo ? doc.nombreArchivo || 'Ver archivo' : 'Subir archivo'}
                    </span>
                    <input
                        type="file"
                        className="d-none"
                        accept="application/pdf,image/jpeg,image/png"
                        disabled={subiendo || !puedeDocumentos}
                        onChange={(e) => handleUploadDocTransaccion(e, docId)}
                    />
                </label>
                {tieneArchivo && docUrl && (
                    <a href={docUrl} target="_blank" rel="noreferrer" className="btn btn-link btn-sm p-0 mt-1 d-flex align-items-center">
                        <FontAwesomeIcon icon={faEye} className="mr-1" /><small>Ver archivo</small>
                    </a>
                )}
            </div>
        );
    }

    return (
        <Card className="shadow-sm bg-light h-100">
            <CardHeader className="py-2">
                <FontAwesomeIcon icon={faShieldAlt} className="text-danger mr-2" />
                <strong className="small text-uppercase">Documentos Transacción</strong>
            </CardHeader>
            <CardBody>
                {/* Contratos */}
                <Row>
                    <Col md="6">
                        {renderDocumentoSingle('contrato_intermediacion', 'Contrato de intermediación comercial')}
                    </Col>
                    <Col md="6">
                        {renderDocumentoSingle('contrato_operacion', 'Contrato de compraventa')}
                    </Col>
                </Row>

                {/* Comprobantes de pago */}
                <Row>
                    <Col md="12">
                        {/* Lista de comprobantes ya subidos */}
                        {(operacion.comprobantes_pago || []).length > 0 && (
                            <div className="mb-3">
                                <p className="small text-muted text-uppercase font-weight-bold mb-1">
                                    Comprobante(s) de pago registrados
                                </p>
                                <ul className="list-unstyled mb-0">
                                    {(operacion.comprobantes_pago || []).map((cp, idx) => {
                                        const cpUrl = cp.fileUrl
                                            ? resolveOperacionDocumentoFetchUrl(unidadId, operacionId, cp)
                                            : null;
                                        return (
                                            <li key={idx} className="d-flex align-items-start mb-2 p-2 border rounded bg-white">
                                                <FontAwesomeIcon
                                                    icon={faCheckCircle}
                                                    className="text-success mr-2 mt-1"
                                                    style={{ flexShrink: 0 }}
                                                />
                                                <div style={{ minWidth: 0, flex: 1 }}>
                                                    {cpUrl ? (
                                                        <a
                                                            href={cpUrl}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                            className="small font-weight-bold text-truncate d-block"
                                                        >
                                                            {cp.nombreArchivo || `Comprobante ${idx + 1}`}
                                                        </a>
                                                    ) : (
                                                        <span className="small font-weight-bold text-muted d-block">
                                                            {cp.nombreArchivo || `Comprobante ${idx + 1}`}
                                                        </span>
                                                    )}
                                                    {cp.forma_pago && (
                                                        <small className="text-muted">
                                                            {cp.forma_pago}
                                                            {cp.forma_pago === 'Efectivo' && cp.forma_pago_monto
                                                                ? ` — $${cp.forma_pago_monto}`
                                                                : ''}
                                                        </small>
                                                    )}
                                                </div>
                                                {puedeDocumentos && (
                                                    <Button
                                                        type="button"
                                                        color="link"
                                                        size="sm"
                                                        className="p-0 ml-2 text-danger"
                                                        title="Eliminar comprobante"
                                                        style={{ flexShrink: 0 }}
                                                        onClick={() => handleDeleteComprobante(idx)}
                                                    >
                                                        <FontAwesomeIcon icon={faTimesCircle} />
                                                    </Button>
                                                )}
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        )}

                        {/* Formulario para agregar un nuevo comprobante */}
                        {puedeDocumentos && (
                            <div className="border rounded p-3 bg-white">
                                <p className="small text-muted text-uppercase font-weight-bold mb-2">
                                    <FontAwesomeIcon icon={faPlus} className="mr-1" />
                                    {(operacion.comprobantes_pago || []).length > 0
                                        ? 'Agregar otro comprobante'
                                        : 'Registrar comprobante de pago'}
                                </p>
                                <Row>
                                    <Col md="6">
                                        <div className="mb-2">
                                            <label className="small text-muted mb-1">Forma de pago *</label>
                                            <select
                                                className="form-control form-control-sm"
                                                value={pendFormaPago}
                                                onChange={(e) => { setPendFormaPago(e.target.value); setPendMonto(''); }}
                                            >
                                                <option value="">Seleccionar…</option>
                                                <option value="Transferencia">Transferencia</option>
                                                <option value="Efectivo">Efectivo</option>
                                                <option value="Cheque">Cheque</option>
                                            </select>
                                        </div>
                                        {pendFormaPago === 'Efectivo' && (
                                            <div className="mb-2">
                                                <label className="small text-muted mb-1">Monto en efectivo</label>
                                                <Input
                                                    type="number"
                                                    bsSize="sm"
                                                    placeholder="Ej. 50000"
                                                    min="0"
                                                    step="any"
                                                    value={pendMonto}
                                                    onChange={(e) => setPendMonto(e.target.value)}
                                                />
                                            </div>
                                        )}
                                    </Col>
                                    <Col md="6" className="d-flex align-items-end" style={{ maxHeight: '64px' }}>
                                        <div className="w-100">
                                        {!pendFormaPago && (
                                                <small className="text-muted d-block text-center">
                                                    Selecciona forma de pago primero
                                                </small>
                                            )}
                                            <label
                                                className={`btn btn-block d-flex justify-content-between align-items-center mb-0${(!pendFormaPago || subiendoDoc === 'comprobantes_pago') ? ' disabled btn-outline-secondary' : ' btn-outline-primary'}`}
                                                style={{ cursor: (!pendFormaPago || subiendoDoc === 'comprobantes_pago') ? 'not-allowed' : 'pointer', fontWeight: 'normal', fontSize: '1rem' }}
                                            >
                                                <span>
                                                    <FontAwesomeIcon
                                                        icon={faUpload}
                                                        spin={subiendoDoc === 'comprobantes_pago'}
                                                        className="mr-1"
                                                    />
                                                    {subiendoDoc === 'comprobantes_pago' ? 'Subiendo…' : 'Adjuntar archivo (JPG / PDF)'}
                                                </span>
                                                <input
                                                    type="file"
                                                    className="d-none"
                                                    accept="application/pdf,image/jpeg,image/png"
                                                    disabled={!pendFormaPago || subiendoDoc === 'comprobantes_pago'}
                                                    onChange={handleUploadComprobantesPago}
                                                />
                                            </label>
                                        </div>
                                    </Col>
                                </Row>
                            </div>
                        )}
                    </Col>
                </Row>
            </CardBody>
        </Card>
    );
}
